<template>
  <div ref="container" class="container">
    <img
      ref="map"
      class="map"
      :src="`/game-content/${gameStateKey}/drone/${map}`"
      @load="onMapLoad"
    />

    <div class="stats-container">
      <div class="stopwatch">{{ time }}s</div>
      <div class="battery">
        <div class="battery-outer">
          <div
            class="battery-level"
            :style="{ width: batteryLevel * 70 + 'px' }"
          ></div>
        </div>
        <div class="battery-bump"></div>
      </div>
    </div>
    <div
      class="no-fly-zone"
      v-for="(zone, index) in computedNoFlyZones"
      :key="index"
      :style="{
        left: zone[0] + 'px',
        top: zone[1] + 'px',
        width: zone[2] + 'px',
        height: zone[3] + 'px',
      }"
    ></div>
    <div
      class="icon-container drone"
      :style="{
        visibility: computedDroneAlive ? 'visible' : 'hidden',
        top: computedDroneCoordinates[1] + 'px',
        left: computedDroneCoordinates[0] + 'px',
      }"
    >
      <img
        ref="drone"
        class="icon"
        :src="
          itemLocation === 1
            ? require('@/assets/drone/drone-with-package.svg')
            : require('@/assets/drone/drone.svg')
        "
      />
    </div>
    <div
      class="icon-container destination"
      :class="{ 'has-item': itemLocation === 2 }"
      :style="{
        top: computedDestinationCoordinates[1] + 'px',
        left: computedDestinationCoordinates[0] + 'px',
      }"
    >
      <img
        ref="destination"
        class="icon"
        src="@/assets/drone/destination.svg"
      />
    </div>
    <div
      class="icon-container warehouse"
      :class="{ 'has-item': itemLocation === 0 }"
      :style="{
        top: computedWarehouseCoordinates[1] + 'px',
        left: computedWarehouseCoordinates[0] + 'px',
      }"
    >
      <img ref="warehouse" class="icon" src="@/assets/drone/warehouse.svg" />
    </div>
    <canvas ref="canvas" class="canvas"></canvas>
  </div>
</template>

<script>
let ctx;

function setUpCanvasDrawing(canvas, shouldBeDrawing, isStartAllowed, onFinish) {
  ctx = canvas.getContext("2d");

  let isDrawing = false;
  let lastX = 0;
  let lastY = 0;

  let path = [];

  function draw(e) {
    if (!isDrawing || !shouldBeDrawing()) return;

    ctx.beginPath();

    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.lineWidth = 10;
    ctx.strokeStyle = "#FFF";

    ctx.moveTo(lastX, lastY);
    ctx.lineTo(e.offsetX, e.offsetY);
    ctx.stroke();

    lastX = e.offsetX;
    lastY = e.offsetY;

    path.push([lastX, lastY]);
  }

  canvas.addEventListener("mousedown", e => {
    if (isStartAllowed(e.offsetX, e.offsetY) && shouldBeDrawing()) {
      isDrawing = true;
      lastX = e.offsetX;
      lastY = e.offsetY;

      path = [];
      path.push([lastX, lastY]);
    }
  });

  canvas.addEventListener("mousemove", draw);
  canvas.addEventListener("mouseup", () => {
    if (isDrawing) onFinish(path);
    isDrawing = false;
  });
  canvas.addEventListener("mouseout", () => {
    if (isDrawing) onFinish(path);
    isDrawing = false;
  });
}

function rescaleValue(val, ogMax, newMax) {
  const result = (val / ogMax) * newMax;

  if (!result || result === Infinity) return 999999;

  return result;
}

function isInBound(x, y, coordinates, element, isPin = false) {
  const isXInBound = Math.abs(coordinates[0] - x) <= element.clientWidth / 2;
  const isYInBound = isPin
    ? coordinates[1] - y >= 0 && coordinates[1] - y <= element.clientHeight
    : Math.abs(coordinates[1] - y) <= element.clientHeight / 2;

  return isXInBound && isYInBound;
}

let stopwatchInterval, startTime;

export default {
  name: "DroneMap",
  components: {},
  props: {
    initialDroneCoordinatesP: Array,
    destinationCoordinatesP: Array,
    warehouseCoordinatesP: Array,
    noFlyZonesP: Array,
    mapP: String,
    gameStateKeyP: String,
    delivering: Boolean,
  },
  data() {
    return {
      initialDroneCoordinates: this.initialDroneCoordinatesP,
      destinationCoordinates: this.destinationCoordinatesP,
      warehouseCoordinates: this.warehouseCoordinatesP,
      noFlyZones: this.noFlyZonesP,
      map: this.mapP,
      gameStateKey: this.gameStateKeyP,
      shouldBeDrawing: true,
      mapNaturalWidth: 0,
      mapNaturalHeight: 0,
      mapWidth: 0,
      mapHeight: 0,
      droneCoordinates: undefined,
      itemLocation: 0, // 0: warehouse, 1: drone, 2: destination
      batteryLevel: 1,
      droneAlive: true,
      time: 0,
    };
  },
  computed: {
    computedDroneAlive() {
      return this.batteryLevel > 0 && this.droneAlive;
    },
    computedDroneCoordinates() {
      if (!this.droneCoordinates) {
        let x = this.initialDroneCoordinates[0];
        let y = this.initialDroneCoordinates[1];

        x = rescaleValue(x, this.mapNaturalWidth, this.mapWidth);
        y = rescaleValue(y, this.mapNaturalHeight, this.mapHeight);

        return [x, y];
      }
      return this.droneCoordinates;
    },
    computedDestinationCoordinates() {
      let x = this.destinationCoordinates[0];
      let y = this.destinationCoordinates[1];

      x = rescaleValue(x, this.mapNaturalWidth, this.mapWidth);
      y = rescaleValue(y, this.mapNaturalHeight, this.mapHeight);

      return [x, y];
    },
    computedWarehouseCoordinates() {
      let x = this.warehouseCoordinates[0];
      let y = this.warehouseCoordinates[1];

      x = rescaleValue(x, this.mapNaturalWidth, this.mapWidth);
      y = rescaleValue(y, this.mapNaturalHeight, this.mapHeight);

      return [x, y];
    },
    computedNoFlyZones() {
      if (this.noFlyZones)
        return this.noFlyZones.map(([x, y, w, h]) => [
          rescaleValue(x, this.mapNaturalWidth, this.mapWidth),
          rescaleValue(y, this.mapNaturalHeight, this.mapHeight),
          rescaleValue(w, this.mapNaturalWidth, this.mapWidth),
          rescaleValue(h, this.mapNaturalHeight, this.mapHeight),
        ]);
      return [];
    },
  },
  watch: {
    delivering(value) {
      if (value) {
        (this.initialDroneCoordinates = this.initialDroneCoordinatesP),
          (this.destinationCoordinates = this.destinationCoordinatesP),
          (this.warehouseCoordinates = this.warehouseCoordinatesP),
          (this.noFlyZones = this.noFlyZonesP),
          (this.map = this.mapP),
          (this.gameStateKey = this.gameStateKeyP),
          (this.shouldBeDrawing = true);
        this.droneCoordinates = undefined;
        this.itemLocation = 0;
        this.batteryLevel = 1;
        this.droneAlive = true;
        this.time = 0;

        startTime = Date.now();
        stopwatchInterval = setInterval(() => {
          this.time = Math.round((Date.now() - startTime) / 1000);
        }, 1000);
      } else {
        clearInterval(stopwatchInterval);
      }
    },
    computedDroneAlive(value) {
      if (!value) clearInterval(stopwatchInterval);
    },
  },
  methods: {
    onMapLoad() {
      this.mapWidth = this.$refs.map.clientWidth;
      this.mapHeight = this.$refs.map.clientHeight;

      this.mapNaturalWidth = this.$refs.map.naturalWidth;
      this.mapNaturalHeight = this.$refs.map.naturalHeight;
    },
    animateDrone(path) {
      const droneWidth = this.$refs.drone.clientWidth;
      const droneHeight = this.$refs.drone.clientHeight;
      const halfDroneWidth = droneWidth / 2;
      const halfDroneHeight = droneHeight / 2;
      const scalingFactor = this.mapNaturalWidth / this.mapWidth;

      const droneAnimationInterval = setInterval(() => {
        if (this.batteryLevel <= 0) {
          this.droneAlive = false;
          ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
          this.$emit("finishDelivery", false, this.time);
          return clearInterval(droneAnimationInterval);
        }

        if (path.length === 0) {
          this.shouldBeDrawing = true;
          ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
          return clearInterval(droneAnimationInterval);
        }

        const firstPoint = path.shift();

        this.droneCoordinates = firstPoint;
        this.batteryLevel -= 0.001 * scalingFactor;

        // check no-fly zones
        if (
          this.computedNoFlyZones.some(
            ([x, y, w, h]) =>
              firstPoint[0] - x >= 0 &&
              firstPoint[1] - y >= 0 &&
              firstPoint[0] - x <= w &&
              firstPoint[1] - y <= h
          )
        ) {
          this.droneAlive = false;
          ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
          this.$emit("finishDelivery", false, this.time);
          return clearInterval(droneAnimationInterval);
        }

        if (
          this.itemLocation === 0 &&
          isInBound(
            firstPoint[0],
            firstPoint[1],
            this.computedWarehouseCoordinates,
            this.$refs.warehouse,
            true
          )
        ) {
          this.itemLocation = 1;
        } else if (
          this.itemLocation === 1 &&
          isInBound(
            firstPoint[0],
            firstPoint[1],
            this.computedDestinationCoordinates,
            this.$refs.destination,
            true
          )
        ) {
          this.itemLocation = 2;
          setTimeout(() => {
            this.$emit("finishDelivery", true, this.time);
          }, 100);
          ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
          return clearInterval(droneAnimationInterval);
        }
      }, 10);
    },
  },
  mounted() {
    const canvas = this.$refs.canvas;
    const resizeObserver = new ResizeObserver(() => {
      this.mapWidth = this.$refs.map.clientWidth;
      this.mapHeight = this.$refs.map.clientHeight;

      canvas.width = this.$refs.container.clientWidth;
      canvas.height = this.$refs.container.clientHeight;
    });

    resizeObserver.observe(this.$refs.container);

    setUpCanvasDrawing(
      canvas,
      () => {
        return this.shouldBeDrawing;
      },
      (x, y) => {
        return isInBound(x, y, this.computedDroneCoordinates, this.$refs.drone);
      },
      path => {
        this.shouldBeDrawing = false;
        this.animateDrone(path);
      }
    );
  },
};
</script>

<style scoped lang="scss">
.container {
  position: relative;

  width: 100%;
  height: 100%;

  overflow: hidden;

  .map {
    width: 100%;
  }

  .no-fly-zone {
    position: absolute;
    background-color: rgba(red, 0.5);
  }

  .stats-container {
    position: absolute;
    top: 16px;
    right: 16px;

    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;

    .stopwatch {
      font-size: 2rem;
      margin-right: 16px;
    }

    .battery {
      display: flex;
      flex-direction: row;
      align-items: center;

      .battery-outer {
        border-radius: 6px;
        border: 2px solid white;
        padding: 2px;
        width: 70px;
        height: 25px;
      }

      .battery-bump {
        border-radius: 2px;
        background-color: white;
        width: 3px;
        height: 15px;
      }

      .battery-level {
        border-radius: 3px;
        background-color: #73ad21;
        width: 70px;
        height: 25px;
      }
    }
  }

  .icon-container {
    position: absolute;
    width: 5%;

    &.has-item::before {
      content: url(../../../assets/drone/package.svg);
    }

    &.drone {
      transform: translate(-50%, -50%);

      animation-duration: 3s;
      animation-name: droneWiggle;
      animation-iteration-count: infinite;
    }
    &.destination {
      transform: translate(-50%, -100%);
    }
    &.warehouse {
      transform: translate(-50%, -100%);
    }
  }

  .canvas {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  }
}

@keyframes droneWiggle {
  from {
    transform: translate(-54%, -54%);
  }

  25% {
    transform: translate(-54%, -46%);
  }

  50% {
    transform: translate(-46%, -46%);
  }

  75% {
    transform: translate(-46%, -54%);
  }

  to {
    transform: translate(-54%, -54%);
  }
}
</style>
